// src/manager/pages/SetCashoutRate.tsx — FINAL COMPLETE VERSION
// Real-time sync, toast feedback, RTP warning, safety limits, clean UI

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { useManager } from '../hooks/useManager';
import { toast } from 'react-hot-toast';

interface FormData {
  cashout_rate: number;
}

export default function SetCashoutRate() {
  const { setCashoutRate, socket } = useManager();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Real-time cashout rate sync
  useEffect(() => {
    if (!socket) return;

    const handleCashoutRateUpdated = () => {
      toast.success('Cashout rate updated in real-time!', { duration: 4000 });
      // Optional: fetch manager data if you have a fetchManager function
    };

    socket.on('cashout-rate-updated', handleCashoutRateUpdated);

    return () => {
      socket.off('cashout-rate-updated', handleCashoutRateUpdated);
    };
  }, [socket]);

  const onSubmit = async (data: FormData) => {
    setError('');
    setLoading(true);

    // Safety validation (same as original)
    if (data.cashout_rate < 0.01 || data.cashout_rate > 100) {
      setError('Cashout rate must be between 0.01 and 100');
      toast.error('Cashout rate must be between 0.01 and 100');
      setLoading(false);
      return;
    }

    // RTP/House exposure warning
    if (!window.confirm('Changing cashout rate will affect house RTP exposure and payout balance. Confirm?')) {
      setLoading(false);
      return;
    }

    try {
      await setCashoutRate(Number(data.cashout_rate));

      reset(); // Clear form
      toast.success(`Cashout rate set to ${data.cashout_rate} successfully!`);
    } catch (err: any) {
      const msg = err.message || 'Failed to set cashout rate';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Set Cashout Rate</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label htmlFor="cashout_rate" className="block text-sm font-medium text-gray-700 mb-1">
              Cashout Rate (Credits per Dollar)
            </label>
            <input
              id="cashout_rate"
              type="number"
              step="0.01"
              placeholder="e.g. 1.00"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
              {...register('cashout_rate', {
                required: 'Cashout rate required',
                min: { value: 0.01, message: 'Minimum 0.01' },
                max: { value: 100, message: 'Maximum 100' },
                valueAsNumber: true,
              })}
            />
            {errors.cashout_rate && <p className="mt-1 text-sm text-red-600">{errors.cashout_rate.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-6 font-medium rounded-lg text-white transition shadow ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin mr-2" size={20} />
                Setting...
              </span>
            ) : (
              'Set Cashout Rate'
            )}
          </button>
        </form>

        {/* RTP/House Note */}
        <p className="mt-6 text-sm text-gray-500 text-center">
          Note: Changing cashout rate affects house RTP exposure and payout balance. Use carefully.
        </p>
      </div>
    </div>
  );
}